import IconSunset from '@assets/images/sundown.svg'
import IconSunrise from '@assets/images/sunset.svg'
import { getPropertyValue } from '@constants/utils'
import Logger from '@logger'
import type { Point } from '@shared_types/config_types'
import { degreesToRadians } from '@turf/helpers'
import { is24HTimeSystem } from '@utils/time_utils'

import { animateAtFPS, ANIMATED_GAUGES_ENABLED } from './animation_settings'

const logger = Logger.create('canvas').disable()

class Canvas {
  private static sunriseImage = document.createElement('img')
  private static sunsetImage = document.createElement('img')
  private static fontFamily: string = ''
  private static secondaryFontFamily: string = ''
  private static meterColor: string = ''

  static drawLine = (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    color: string,
    width: number = 2
  ) => {
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(startX, startY)
    ctx.lineTo(endX, endY)
    ctx.stroke()
  }

  static drawArc = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    color: string,
    width?: number,
    fillColor?: string
  ) => {
    ctx.strokeStyle = color
    ctx.fillStyle = fillColor || 'transparent'
    ctx.lineWidth = width || 1
    ctx.beginPath()
    radius > 0 && ctx.arc(centerX, centerY, radius, startAngle, endAngle)
    ctx.fill()
    ctx.stroke()
  }

  static drawBoat = (
    ctx: CanvasRenderingContext2D,
    dx: number,
    dy: number,
    width: number,
    height: number,
    angle: number = 0
  ) => {
    const TO_RADIANS = Math.PI / 180

    // save the current co-ordinate system before we screw with it
    ctx.save()

    // move to the middle of where we want to draw our image
    ctx.translate(dx + width / 2, dy + height / 2)

    // rotate around that point, converting our
    // angle from degrees to radians
    ctx.rotate(angle * TO_RADIANS)

    // draw it up and to the left by half the width
    // and height of the image

    ctx.strokeStyle = getPropertyValue('--primary-color')
    ctx.lineWidth = 0.25
    ctx.fillStyle = getPropertyValue('--primary-color')
    const p1 = new Path2D(
      'M.5,22.6a.5.5,0,0,0,.5-.5V21C1,16.19,4.88,5.49,6.24,1.89,7.6,5.47,11.47,16.06,11.47,21V22.1a.5.5,0,0,0,.5.5.5.5,0,0,0,.5-.5V21C12.47,14.84,6.93.9,6.7.31A.5.5,0,0,0,6.23,0h0a.5.5,0,0,0-.46.32C5.53.91,0,15,0,21V22.1A.5.5,0,0,0,.5,22.6Z'
    )
    const p2 = new Path2D(
      'M12,37.69a.5.5,0,0,0-.5.5v4.53c-.47,3.91-1.35,8.39-2,9H3c-.6-.6-1.49-5.07-2-8.9V38.19a.5.5,0,0,0-.5-.5.5.5,0,0,0-.5.5v4.65c1.26,9.84,2.49,9.84,2.89,9.84H9.57c1.13,0,2.1-3.31,2.9-9.9V38.19A.5.5,0,0,0,12,37.69Z'
    )

    ctx.beginPath()
    ctx.translate(-width * 4.75, -width * 20)
    ctx.scale(width * 0.75, width * 0.75)
    ctx.closePath()

    ctx.stroke(p1)
    ctx.fill(p1)
    ctx.stroke(p2)
    ctx.fill(p2)

    // and restore the co-ords to how they were when we began
    ctx.restore()
  }

  static drawMecca = (ctx: CanvasRenderingContext2D, dx: number, dy: number, size: number) => {
    // save the current co-ordinate system before we screw with it
    ctx.save()

    ctx.translate(dx + size / 2, dy + size / 2)

    ctx.strokeStyle = getPropertyValue('--primary-color')
    ctx.lineWidth = 0.5
    ctx.fillStyle = getPropertyValue('--primary-color')

    const p = new Path2D(
      'M.754,21.936V7.89l11.461,7V28.94Zm12.215-7.042,11.461-7V21.936l-11.461,7ZM1.125,7.234,12.592.864l11.466,6.37L12.592,14.241ZM12.592,0,0,7V22.359l12.592,7.695,12.592-7.695V7Z'
    )

    ctx.beginPath()
    ctx.translate(-size * 2, -size * 2)
    ctx.scale(size * 0.15, size * 0.15)
    ctx.closePath()

    ctx.stroke(p)
    ctx.fill(p)

    ctx.restore()
  }

  static drawSun = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    isBig?: boolean
  ) => {
    if (Canvas.sunriseImage) {
      const delta = isBig ? 30 : 15
      ctx.save()
      ctx.drawImage(Canvas.sunriseImage, centerX - delta, centerY - delta, radius * 2.0, radius * 1.3)
    }

    if (Math.random() > 1) {
      console.log({
        ctx,
        centerX,
        centerY,
        radius,
        startAngle,
        endAngle,
        isBig
      })
    }
  }

  static drawMoon = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    isBig?: boolean
  ) => {
    if (Canvas.sunsetImage) {
      const [dx, dy] = isBig ? [25, 30] : [10, 10]

      ctx.save()
      ctx.drawImage(Canvas.sunsetImage, centerX - dx, centerY - dy, radius * 2.0, radius * 1.3)
    }

    if (Math.random() > 1) {
      console.log({
        ctx,
        centerX,
        centerY,
        radius,
        startAngle,
        endAngle,
        isBig
      })
    }
  }

  static drawPieSlice = (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    color: string
  ) => {
    ctx.fillStyle = color
    ctx.beginPath()
    ctx.moveTo(centerX, centerY)
    radius > 0 && ctx.arc(centerX, centerY, radius, startAngle, endAngle)
    ctx.closePath()
    ctx.fill()
  }

  static drawPolygon = (ctx: CanvasRenderingContext2D, a: Point, b: Point, c: Point, color?: string) => {
    ctx.fillStyle = color || '#ffffff'
    ctx.beginPath()
    ctx.moveTo(a.x, a.y)
    ctx.lineTo(b.x, b.y)
    ctx.lineTo(c.x, c.y)
    ctx.lineTo(a.x, a.y)
    ctx.closePath()
    ctx.fill()
  }

  static xy (angle: number, xy: number, r: number) {
    return {
      x: xy + r * Math.sin(degreesToRadians(angle)),
      y: xy - r * Math.cos(degreesToRadians(angle))
    }
  }

  static x2 (angle: number, x1: number, r: number) {
    return x1 + r * Math.sin(degreesToRadians(angle))
  }

  static y2 (angle: number, y1: number, r: number) {
    return y1 - r * Math.cos(degreesToRadians(angle))
  }

  static drawUnit = (
    ctx: CanvasRenderingContext2D,
    center: number,
    cy: number,
    unit: string,
    fontSize: number = 14,
    color?: string
  ) => {
    ctx.fillStyle = color || '#ffffff'
    ctx.font = `normal ${fontSize}px ${Canvas.fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(unit, center, cy)
  }

  static drawValue = (
    ctx: CanvasRenderingContext2D,
    center: number,
    cy: number,
    value: string,
    fontSize: number = 20,
    color?: string
  ) => {
    ctx.fillStyle = color || '#eff0f4'
    ctx.font = `500 ${fontSize}px ${Canvas.fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(value, center, cy)
  }

  static drawText = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    value: string,
    fontSize: number = 16,
    color: string = '#7E7E7E'
  ) => {
    ctx.fillStyle = color
    ctx.font = `normal ${fontSize}px ${Canvas.secondaryFontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(value, x, y)
  }

  static drawTitle = (ctx: CanvasRenderingContext2D, center: number, title: string, fontSize: number = 14) => {
    ctx.fillStyle = '#dbdbdb'
    ctx.font = `normal ${fontSize}px ${Canvas.secondaryFontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const index = title.indexOf(' ')
    if (index !== -1) {
      let y = center + center / 3
      ctx.fillText(title.slice(0, index), center, y)
      y += fontSize
      ctx.fillText(title.slice(index), center, y)
    } else {
      ctx.fillText(title, center, center + center / 2.5)
    }
  }

  static drawWave = (ctx: CanvasRenderingContext2D, dpx: number, width: number, height: number, color: string) => {
    ctx.beginPath()
    ctx.lineWidth = 2
    ctx.strokeStyle = color

    let x = dpx
    let y = 0
    const amplitude = height * 0.07
    const frequency = height * 0.066
    while (x < width) {
      y = height / 2 + amplitude * Math.sin(x / frequency)
      ctx.lineTo(x, y)
      x = x + 1
    }
    ctx.stroke()
  }

  static drawTimeValue = (
    ctx: CanvasRenderingContext2D,
    center: number,
    hours: string,
    minutes: string,
    fontSize: number = 20,
    dpx: number = 0,
    color: string = '#eff0f4'
  ) => {
    const offset = 5
    const y = center
    // const value = this.currentValue ? ctx.measureText(formatter(this.currentValue)) : ctx.measureText('0')
    ctx.fillStyle = color
    ctx.font = `normal ${fontSize}px ${Canvas.fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const sepWidth = ctx.measureText(':').width
    ctx.fillText(':', center, y - fontSize / 14)
    ctx.textAlign = 'right'
    ctx.fillText(hours, center - offset, y)
    ctx.textAlign = 'left'

    if (is24HTimeSystem()) {
      ctx.fillText(minutes, center + offset, y)
    } else {
      const min = minutes.substring(0, minutes.length - 3)
      const amPm = minutes.slice(-2)
      const minWidth = ctx.measureText(min).width
      ctx.fillText(min, center + offset, y)
      ctx.font = `normal ${fontSize / 1.55}px ${Canvas.fontFamily}`
      ctx.fillText(amPm, center + offset + sepWidth / 2 + minWidth, y + dpx)
    }
  }

  static drawHand (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    lineWidth: number = 4,
    color: string = Canvas.meterColor
  ) {
    ctx.strokeStyle = color
    ctx.lineWidth = lineWidth
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(startX, startY)
    ctx.lineTo(endX, endY)
    ctx.stroke()
  }

  static drawing = (draw: () => void) => {
    draw()
  }

  static animate (
    currentValue: number,
    nextValue: number,
    drawingFunction: (current: number) => void,
    step: number = 2,
    name: string
  ): number {
    if (!ANIMATED_GAUGES_ENABLED) {
      return -1
    } else if (document.location.pathname.endsWith('/3d_view') && !document.location.search.includes('tv')) {
      return -1 // disable gauge animation in 3D view (when TV mode is OFF)
    }

    logger.debug(`animate ${name} ${currentValue} => ${nextValue}`)

    let req = -1
    let needsAnimation
    drawingFunction(currentValue)

    if (currentValue > nextValue) {
      currentValue -= step
      needsAnimation = currentValue > nextValue
    } else {
      currentValue += step
      needsAnimation = currentValue < nextValue
    }

    if (!needsAnimation) {
      drawingFunction(nextValue)
    } else if (Math.random() > 1) {
      req = animateAtFPS(() => Canvas.animate(currentValue, nextValue, drawingFunction, step, name))
    } else {
      req = requestAnimationFrame(() => Canvas.animate(currentValue, nextValue, drawingFunction, step, name))
    }

    return req
  }

  static setupCanvas = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d')
    const rect = canvas.getBoundingClientRect() // Get the size of the canvas in CSS pixels.
    const dpr = window.devicePixelRatio || 1 // Get the device pixel ratio, falling back to 1.

    // Set display size (css pixels).
    const size = rect.width

    // Set actual size in memory (scaled to account for extra pixel density).
    canvas.width = size * dpr
    canvas.height = size * dpr

    // Normalize coordinate system to use css pixels.
    if (ctx) {
      ctx.scale(dpr, dpr)
    }

    Canvas.fontFamily = getPropertyValue('--font-family')
    Canvas.secondaryFontFamily = getPropertyValue('--secondary-font-family')
    Canvas.meterColor = getPropertyValue('--meter-color')
    Canvas.sunriseImage.src = IconSunrise
    Canvas.sunsetImage.src = IconSunset

    return { ctx, size }
  }
}

export default Canvas
